import React, { useState } from 'react';
import axios from 'axios';
import { Briefcase, Loader2, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Request a quote" (B2B) modal — opens from the product page.
 * Posts to /api/quotes with the user's company info + the product + quantity.
 */
export const QuoteRequestButton = ({ product }) => {
  const { token, isAuthenticated, user } = useAuth();
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: user ? `${user.first_name} ${user.last_name}` : '',
    contact_email: user?.email || '',
    contact_phone: '',
    siret: '',
    quantity: 10,
    target_delivery: '',
    additional_notes: '',
  });

  const submit = async () => {
    if (!form.company_name || !form.contact_email || !form.quantity) {
      toast.error(T('Champs obligatoires manquants', 'Required fields missing'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/quotes`,
        {
          company_name: form.company_name,
          contact_name: form.contact_name,
          contact_email: form.contact_email,
          contact_phone: form.contact_phone || null,
          siret: form.siret || null,
          items: [{ product_id: product.id, quantity: parseInt(form.quantity, 10), notes: form.additional_notes || null }],
          target_delivery: form.target_delivery || null,
          additional_notes: form.additional_notes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(T('Demande de devis envoyée. Le vendeur vous répondra par e-mail.', 'Quote request sent. The vendor will reply by email.'));
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur', 'Error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-sm font-semibold text-[#0F4C5C] hover:text-[#E9C46A] transition-colors flex items-center gap-1.5"
          data-testid="quote-request-btn"
        >
          <Briefcase className="w-4 h-4" />
          {T('Demande de devis (B2B)', 'Request quote (B2B)')}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-[#E9C46A]" />
            {T('Demande de devis professionnel', 'Professional quote request')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-[#0F4C5C]/70">
            {T(
              `Pour ${product.name_fr || product.name} — quantités importantes, prix HT, conditions personnalisées.`,
              `For ${product.name_en || product.name} — bulk quantities, ex-VAT pricing, custom terms.`
            )}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>{T('Nom de l\'entreprise', 'Company name')} *</Label>
              <Input
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="mt-1.5"
                data-testid="quote-company"
              />
            </div>
            <div>
              <Label>{T('Contact', 'Contact')} *</Label>
              <Input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>{T('Email pro', 'Business email')} *</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="mt-1.5"
                data-testid="quote-email"
              />
            </div>
            <div>
              <Label>{T('Téléphone', 'Phone')}</Label>
              <Input
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>SIRET</Label>
              <Input
                value={form.siret}
                onChange={(e) => setForm({ ...form, siret: e.target.value })}
                placeholder="14 chiffres"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>{T('Quantité', 'Quantity')} *</Label>
              <Input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                min={1}
                className="mt-1.5"
                data-testid="quote-quantity"
              />
            </div>
            <div>
              <Label>{T('Livraison souhaitée', 'Desired delivery')}</Label>
              <Input
                type="date"
                value={form.target_delivery}
                onChange={(e) => setForm({ ...form, target_delivery: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div className="col-span-2">
              <Label>{T('Notes complémentaires', 'Additional notes')}</Label>
              <Textarea
                value={form.additional_notes}
                onChange={(e) => setForm({ ...form, additional_notes: e.target.value })}
                rows={3}
                placeholder={T('Conditions, contraintes, contexte...', 'Conditions, constraints, context...')}
                className="mt-1.5"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{T('Annuler', 'Cancel')}</Button>
          <Button onClick={submit} disabled={loading} className="btn-primary" data-testid="quote-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {T('Envoyer la demande', 'Send request')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
